import {
  ResponsiveContainer,
  Tooltip,
  BarChart as RechartsBarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
} from 'recharts';
import { BarChartProps } from './props';
import { useTheme } from 'styled-components';
import { useMemo } from 'react';
import { BarChartTooltip } from './Tooltip';

/**
 * BarChart component that renders a customizable bar chart using Recharts
 *
 * @template T - Generic type parameter for the data shape
 * @param {Object} props - Component props
 * @param {T[]} props.data - Array of data points to display in the chart
 * @param {Object} props.dataKey - Object containing keys for x-axis, y-axis and bar data
 * @param {string} props.dataKey.x - Key for x-axis data
 * @param {string} props.dataKey.y - Key for y-axis data
 * @param {string} props.dataKey.line - Key for bar data values
 * @param {boolean} props.showAxis - Whether to show x and y axes
 * @returns {JSX.Element} Responsive bar chart component
 */
export function BarChart<T>({ data, dataKey, showAxis }: BarChartProps<T>) {
  // Get theme for styling
  const theme = useTheme();

  // Memoized cell components with conditional coloring based on value
  const Cells = useMemo(
    () =>
      data?.map((entry, index) => {
        return (
          <Cell
            key={`cell-${index}`}
            fill={
              Number(entry?.amount) < 0
                ? theme.vars.palette.error.light // Red for negative values
                : theme.vars.palette.success.main // Green for positive values
            }
            strokeWidth={1}
          />
        );
      }),
    [data, theme],
  );

  return (
    <ResponsiveContainer width="100%" height="100%">
      <RechartsBarChart
        data={data}
        margin={{ left: 0, right: 0, bottom: 0, top: 0 }}
        {...{ overflow: 'visible' }}
      >
        {/* Bar component displaying the main chart data */}
        <Bar dataKey={dataKey.line}>{Cells}</Bar>

        {/* Conditionally render axes if showAxis is true */}
        {showAxis && (
          <>
            <XAxis
              dataKey={dataKey.x}
              tick={{
                stroke: theme.vars.palette.text.secondary,
                strokeWidth: 0,
                fontSize: '12px',
                fontWeight: '400',
              }}
            />
            <YAxis
              dataKey={dataKey.y}
              width={30}
              tick={{
                stroke: theme.vars.palette.text.secondary,
                strokeWidth: 0,
                fontSize: '12px',
                fontWeight: '400',
              }}
            />
          </>
        )}
        {/* Custom tooltip component */}
        <Tooltip cursor={false} content={<BarChartTooltip />} />
      </RechartsBarChart>
    </ResponsiveContainer>
  );
}
